"use client"

import { useCallback, useMemo, useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { X } from "lucide-react"

import { PeriodSelector } from "@/components/ui/analytics/period-selector"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuth } from "@/hooks/auth"
import { useQualityAccessQuery, useQualitySuggestionsQuery } from "@/hooks/queries/conversation-quality"
import type { AnalyticsPeriod } from "@/lib/analytics/period"
import { qualityOffNotice } from "@/lib/conversation-quality/access-display"
import {
  QUALITY_TAB_LANDINGS,
  parseQualityPageTab,
  type QualityPageTab,
  type QualityTabLanding,
} from "@/lib/conversation-quality/page-tabs"
import { formatReportWeek, isReportWeekStart } from "@/lib/conversation-quality/report-week"
import type { QualityAccessResult } from "@/server-actions/conversation-quality-access"

import { QualityAccessSwitch } from "./quality-access-switch"
import { QualityAgentSelect } from "./quality-agent-select"
import { HandoversTab } from "./tabs/handovers-tab"
import { LearningTab } from "./tabs/learning-tab"
import { OverviewTab } from "./tabs/overview-tab"
import { ErrorAlert, Notice, type QualityState, type QualityTabProps } from "./tabs/tab-parts"
import { VolumeTab } from "./tabs/volume-tab"

/**
 * Four jobs, one tab each: how the agent did, what it learned and what waits for a decision, whether the team answers
 * handovers, and how much came in. Only the open tab is mounted, so the period's queries run for it alone.
 */
export function ConversationQualityPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { user } = useAuth()

  const agentId = searchParams.get("agentId")
  const tab = parseQualityPageTab(searchParams.get("tab"))
  const week = reportWeek(searchParams.get("week"))
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d")
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null)

  const viewerTimezone = user?.timezone ?? "UTC"
  // A weekly report's week is a UTC week: its days, trend and labels read it as the report did.
  const timezone = week ? "UTC" : viewerTimezone
  const input = useMemo(
    () => ({
      agentId,
      period,
      customRange: customRange ?? undefined,
      week: week ?? undefined,
      timezone,
    }),
    [agentId, period, customRange, week, timezone]
  )

  // The sections the feature produces load only once the workspace is known to have it on;
  // otherwise every load would fire refused requests and their retries.
  const { data: access, error: accessError } = useQualityAccessQuery()
  const quality = qualityState(access, accessError)
  const tabProps: QualityTabProps = { input, viewerTimezone, ready: Boolean(user), quality }
  // Counted on its tab whichever tab is open; Learning & improvements reads the same cache.
  const { data: suggestions } = useQualitySuggestionsQuery({ agentId }, quality === "on")
  const waiting = suggestions?.waiting ?? 0

  // The agent and a report's week live in the URL so a filtered view can be shared and survives a reload.
  const replaceParam = useCallback(
    (name: "agentId" | "week", next: string | null) => {
      const params = new URLSearchParams(searchParams.toString())
      if (next) params.set(name, next)
      else params.delete(name)
      router.replace(`?${params.toString()}`, { scroll: false })
    },
    [router, searchParams]
  )
  const selectAgent = useCallback((next: string | null) => replaceParam("agentId", next), [replaceParam])
  const leaveReportWeek = useCallback(() => replaceParam("week", null), [replaceParam])

  // Next.js keeps `useSearchParams` in step with the History API, so the tab needs no state of its own.
  // A switch without a landing clears any left, so no tab lands twice.
  const selectTab = useCallback((next: QualityPageTab, landing: QualityTabLanding = {}) => {
    const url = new URL(window.location.href)
    url.searchParams.set("tab", next)
    for (const param of QUALITY_TAB_LANDINGS) {
      const value = landing[param]
      if (value) url.searchParams.set(param, value)
      else url.searchParams.delete(param)
    }
    window.history.replaceState(null, "", url.toString())
  }, [])

  const handlePeriodChange = useCallback(
    (next: AnalyticsPeriod, range?: { from: string; to: string }) => {
      setPeriod(next)
      setCustomRange(range ?? null)
    },
    []
  )

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-3 sm:space-y-6 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold sm:text-2xl">Conversation Quality</h1>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <QualityAgentSelect
            agentId={agentId}
            onChange={selectAgent}
            className="w-full sm:w-[200px]"
          />
          {week ? (
            <ReportWeek week={week} onLeave={leaveReportWeek} />
          ) : (
            <PeriodSelector
              period={period}
              customRange={customRange}
              onPeriodChange={handlePeriodChange}
              className="w-full sm:w-auto"
              triggerClassName="w-full sm:w-[180px]"
            />
          )}
          {access?.canChange && <QualityAccessSwitch enabled={access.enabled} />}
        </div>
      </div>

      <AccessNotice access={access} error={accessError} />

      <Tabs value={tab} onValueChange={(value) => selectTab(parseQualityPageTab(value))}>
        {/* Its own scroll: the four names outgrow a tablet's column beside the sidebar. */}
        <TabsList className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="learning">
            <span>Learning &amp; improvements</span>
            {waiting > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs tabular-nums">
                {waiting}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="handovers">Handovers &amp; team</TabsTrigger>
          <TabsTrigger value="volume">Volume</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4 sm:mt-6">
          <OverviewTab {...tabProps} onOpenTab={selectTab} />
        </TabsContent>
        <TabsContent value="learning" className="mt-4 sm:mt-6">
          <LearningTab {...tabProps} />
        </TabsContent>
        <TabsContent value="handovers" className="mt-4 sm:mt-6">
          <HandoversTab {...tabProps} />
        </TabsContent>
        <TabsContent value="volume" className="mt-4 sm:mt-6">
          <VolumeTab {...tabProps} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function qualityState(access: QualityAccessResult | undefined, error: Error | null): QualityState {
  if (error || access?.enabled === false) return "off"
  return access ? "on" : "loading"
}

/**
 * Off, the sections the feature produces give way to this; volume and the service metrics need
 * no model and stay. The tenant's own switch, where the caller may flip it, sits in the header.
 */
function AccessNotice({
  access,
  error,
}: {
  access: QualityAccessResult | undefined;
  error: Error | null;
}) {
  if (error) return <ErrorAlert error={error} />
  if (!access?.disabledBy) return null
  return <Notice>{qualityOffNotice(access.disabledBy)}</Notice>
}

/** An invalid week is no week: the page falls back to its period. */
function reportWeek(value: string | null): string | null {
  return value !== null && isReportWeekStart(value) ? value : null
}

/** In place of the period while a weekly report's link is open; leaving it brings the period back. */
function ReportWeek({ week, onLeave }: { week: string; onLeave: () => void }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-1 rounded-md border px-3 py-1.5 text-sm sm:justify-start">
      <span className="min-w-0 break-words">{`Report week: ${formatReportWeek(week)} (UTC)`}</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0"
        onClick={onLeave}
        aria-label="Show a period instead"
        title="Show a period instead"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
    </div>
  )
}
